import React, {useMemo} from 'react';

import moment from 'moment-tz';

import {useReassignTaskTemplate} from 'app/tasks/api';
import {gt} from 'app/utils/i18n';

import {LoadTaskForReassignModal} from './LoadTaskForReassignModal';

import {BaseAssignTaskModal} from '../BaseAssignTaskModal';

import type {
  SlimTaskInstanceSerializedType,
  TaskInstanceSerializedType,
  TaskTemplateSerializedType,
  WriteTaskTemplateSerializedType,
} from 'sweet-types';

interface CommonProps {
  show: boolean;
  onToggle: () => unknown;
  applyToAllFutureInstances?: boolean;
}

interface TaskInstanceProps extends CommonProps {
  taskInstance: SlimTaskInstanceSerializedType | TaskInstanceSerializedType;
  taskTemplate?: never;
}

interface TaskTemplateProps extends CommonProps {
  taskTemplate: TaskTemplateSerializedType;
  taskInstance?: never;
}

type Props = TaskInstanceProps | TaskTemplateProps;

export function ReassignTaskModal({
  taskInstance,
  taskTemplate,
  applyToAllFutureInstances = false,
  ...props
}: Props) {
  const [reassignTaskTemplate] = useReassignTaskTemplate();

  const dueFieldsPartials = calculateDueAndDtStart(
    (taskInstance ?? taskTemplate).due,
    (taskInstance ?? taskTemplate).dtstart,
    applyToAllFutureInstances
  );

  return (
    <LoadTaskForReassignModal
      taskInstance={taskInstance}
      taskTemplate={taskTemplate}
      {...props}
    >
      {({task: baseTask, assignedDoc, assignments}) => (
        <BaseAssignTaskModal
          title={gt('Edit Task')}
          canChangeDoc={false}
          canMultiAssign={false}
          taskData={{
            ...baseTask,
            ...assignedDoc,
            ...dueFieldsPartials,
          }}
          assignmentsData={assignments}
          lockDueDate={applyToAllFutureInstances}
          prependToDueDateForm={
            applyToAllFutureInstances ? (
              <div className="alert alert-primary small">
                {gt(
                  'Your changes will be applied to all future tasks in this series'
                )}
              </div>
            ) : null
          }
          actionButtonText={[gt('Update Task'), gt('Updating...')]}
          assignAction={({task}) =>
            reassignTaskTemplate({
              instanceURL: taskInstance?.url,
              task: {...task, task: baseTask.task},
              until: applyToAllFutureInstances
                ? moment().toISOString()
                : baseTask.due,
            })
          }
          {...props}
        />
      )}
    </LoadTaskForReassignModal>
  );
}

function calculateDueAndDtStart(
  due: string | undefined,
  dtstart: string | null | undefined,
  applyToAllFutureInstances: boolean
): Pick<WriteTaskTemplateSerializedType, 'due' | 'dtstart'> {
  return useMemo(() => {
    if (!applyToAllFutureInstances) {
      return {due: due ?? '', dtstart};
    }

    const now = moment.tz();
    const nowDue = moment(due).set({
      year: now.year(),
      month: now.month(),
      date: now.date(),
    });
    const dtstartDiff = moment(due).diff(moment(dtstart));
    const nowDtstart = nowDue.clone().subtract(dtstartDiff);
    return {
      due: nowDue.toISOString(),
      dtstart: nowDtstart.toISOString(),
    };
  }, [due, dtstart, applyToAllFutureInstances]);
}
